/**
 * The implied tanpura: a quiet Sa sub-drone (sine + 2nd/3rd partial) an octave below the tonic, ~14 dB under a typical
 * voice. Voices sit around −26 dBFS RMS (samples at −18 LUFS through their chains), so the drone's peak is set near
 * −34 dBFS; the listening harness measured the old −14 dBFS setting as louder than the voices themselves.
 */
import { dbToGain, glideTo, targetTau } from './params';
import { safeStop } from './units';

const PARTIALS: Array<[number, number]> = [[1, 1], [2, 0.45], [3, 0.25]];

export class Tanpura {
  private readonly oscs: OscillatorNode[] = [];
  private readonly gains: GainNode[] = [];
  private readonly sum: GainNode;
  private readonly lp: BiquadFilterNode;
  private readonly env: GainNode;
  private hz = 0;
  private _active = false;

  constructor(private readonly ctx: AudioContext, destination: AudioNode) {
    this.sum = ctx.createGain();
    this.sum.gain.value = dbToGain(-34);
    this.lp = ctx.createBiquadFilter();
    this.lp.type = 'lowpass';
    this.lp.frequency.value = 1500;
    this.env = ctx.createGain();
    this.env.gain.value = 0;
    this.sum.connect(this.lp);
    this.lp.connect(this.env);
    this.env.connect(destination);
    const now = ctx.currentTime;
    for (const [, amp] of PARTIALS) {
      const o = ctx.createOscillator();
      o.type = 'sine';
      const g = ctx.createGain();
      g.gain.value = amp;
      o.connect(g);
      g.connect(this.sum);
      o.start(now);
      this.oscs.push(o);
      this.gains.push(g);
    }
  }

  get active(): boolean { return this._active; }

  /** Sub-drone fundamental (already an octave below the tonic). Glides over 2 s when re-tuned. */
  setHz(hz: number, t: number): void {
    if (!(hz > 0)) return;
    const first = this.hz === 0;
    this.hz = hz;
    PARTIALS.forEach(([ratio], i) => {
      const o = this.oscs[i]!;
      if (first) o.frequency.setValueAtTime(hz * ratio, t);
      else glideTo(o.frequency, hz * ratio, t, 2);
    });
  }

  fadeIn(t: number): void {
    if (this._active || this.hz === 0) return;
    this._active = true;
    targetTau(this.env.gain, 1, t, 2 / 4.6);
  }

  fadeOut(t: number, seconds = 6): void {
    if (!this._active) return;
    this._active = false;
    targetTau(this.env.gain, 0, t, seconds / 4.6);
  }

  dispose(): void {
    const now = this.ctx.currentTime;
    for (const o of this.oscs) safeStop(o, now);
    for (const n of [...this.oscs, ...this.gains, this.sum, this.lp, this.env]) { try { n.disconnect(); } catch { /* gone */ } }
  }
}
